import {
  QUALIFYING_SCORE_HEIGHT_LABELS,
  QUALIFYING_SCORE_HEIGHT_OPTIONS,
} from "../constants/data";

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const heightLabel = (value) => {
  if (value == null) return "";
  return QUALIFYING_SCORE_HEIGHT_LABELS[Number(value)] || String(value);
};

export const heightOptions = () => QUALIFYING_SCORE_HEIGHT_OPTIONS;

export const parseIntOrNull = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
};

export const parseFloatOrNull = (value) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Round to 2 decimal places. Adds Number.EPSILON to dodge the classic
// 1.005 → 1.00 floating-point trap.
export const roundToTwo = (value) => {
  if (value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round((n + Number.EPSILON) * 100) / 100;
};

export const toNullableString = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
};

// Accept comma, semicolon, whitespace, or newline as splitters; trim + dedupe case-insensitively.
export const splitRecipients = (raw) => {
  if (!raw) return [];
  const parts = String(raw)
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return dedupeRecipients(parts);
};

export const dedupeRecipients = (list) => {
  const seen = new Set();
  const out = [];
  (list || []).forEach((raw) => {
    const value = String(raw || "").trim();
    if (!value) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(value);
  });
  return out;
};

export const isValidEmail = (value) => EMAIL_REGEX.test(String(value || "").trim());

const POINTS_REGEX = /^\d+(\.\d+)?$/;

export const validateQualifyingScoreForm = (values) => {
  const errors = {};

  if (!toNullableString(values?.ageGroup)) errors.ageGroup = "Required";
  if (!toNullableString(values?.gender)) errors.gender = "Required";

  const height = Number(values?.eventHeight);
  if (![1, 3, 10].includes(height)) errors.eventHeight = "Choose 1m, 3m, or Platform";

  const diveCount = parseIntOrNull(values?.diveCount);
  if (diveCount == null || diveCount <= 0) errors.diveCount = "Must be a positive integer";

  const pointsStr = String(values?.points ?? "").trim();
  if (!pointsStr) {
    errors.points = "Required";
  } else if (!POINTS_REGEX.test(pointsStr)) {
    errors.points = "Must be a number";
  } else {
    const pointsNum = roundToTwo(pointsStr);
    if (pointsNum == null || pointsNum < 1 || pointsNum > 999.99) {
      errors.points = "Must be between 1.00 and 999.99";
    }
  }

  if (values?.minimumDd != null && String(values.minimumDd).trim() !== "") {
    const minDd = Number(values.minimumDd);
    if (!Number.isFinite(minDd) || minDd < 0) errors.minimumDd = "Must be a positive number";
  }

  if (values?.attemptsRequired != null && String(values.attemptsRequired).trim() !== "") {
    const attempts = parseIntOrNull(values.attemptsRequired);
    if (attempts == null || attempts < 0) errors.attemptsRequired = "Must be ≥ 0";
  }

  const fromDate = toNullableString(values?.fromDate);
  const toDate = toNullableString(values?.toDate);
  if (!fromDate) errors.fromDate = "Required";
  if (!toDate) errors.toDate = "Required";
  if (fromDate && toDate && fromDate > toDate) {
    errors.toDate = "Must be on or after From date";
  }

  if (parseIntOrNull(values?.messageTemplateId) == null) {
    errors.messageTemplateId = "Required";
  }

  const recipients = Array.isArray(values?.emailRecipients) ? values.emailRecipients : [];
  const invalid = recipients.filter((r) => !isValidEmail(r));
  if (invalid.length > 0) {
    errors.emailRecipients = `Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`;
  }

  return errors;
};

export const buildSavePayload = (values, selectedDto) => {
  const id = selectedDto?.id ?? null;
  const recipients = dedupeRecipients(
    (Array.isArray(values?.emailRecipients) ? values.emailRecipients : []).map((r) =>
      String(r || "").trim().toLowerCase(),
    ),
  );

  return {
    id,
    governingBodyId: parseIntOrNull(values?.governingBodyId),
    ageGroup: toNullableString(values?.ageGroup),
    gender: toNullableString(values?.gender),
    eventHeight: parseIntOrNull(values?.eventHeight),
    diveCount: parseIntOrNull(values?.diveCount),
    points: roundToTwo(values?.points),
    fromDate: toNullableString(values?.fromDate),
    toDate: toNullableString(values?.toDate),
    minimumDd: parseFloatOrNull(values?.minimumDd),
    attemptsRequired: parseIntOrNull(values?.attemptsRequired) ?? 0,
    conferenceId: parseIntOrNull(values?.conferenceId),
    divisionId: toNullableString(values?.divisionId),
    regionId: toNullableString(values?.regionId),
    // Not on the form, but the save replaces every field, so it must be sent back.
    state: toNullableString(values?.state),
    messageTemplateId: parseIntOrNull(values?.messageTemplateId),
    emailRecipients: recipients,
  };
};

export const emptyQualifyingScoreForm = (governingBodyId) => ({
  governingBodyId: governingBodyId ?? "",
  ageGroup: "",
  gender: "",
  eventHeight: "",
  diveCount: "",
  points: "",
  fromDate: "",
  toDate: "",
  minimumDd: "",
  attemptsRequired: 0,
  conferenceId: "",
  divisionId: "",
  regionId: "",
  state: "",
  messageTemplateId: "",
  emailRecipients: [],
});

export const fromDtoToForm = (dto) => ({
  governingBodyId: dto?.governingBodyId ?? "",
  ageGroup: dto?.ageGroup ?? "",
  gender: dto?.gender ?? "",
  eventHeight: dto?.eventHeight ?? "",
  diveCount: dto?.diveCount ?? "",
  points: dto?.points != null ? Number(dto.points).toFixed(2) : "",
  fromDate: dto?.fromDate ?? "",
  toDate: dto?.toDate ?? "",
  minimumDd: dto?.minimumDd ?? "",
  attemptsRequired: dto?.attemptsRequired ?? 0,
  conferenceId: dto?.conferenceId ?? "",
  divisionId: dto?.divisionId ?? "",
  regionId: dto?.regionId ?? "",
  state: dto?.state ?? "",
  messageTemplateId: dto?.messageTemplateId ?? "",
  emailRecipients: Array.isArray(dto?.emailRecipients) ? [...dto.emailRecipients] : [],
});

// Save request that rewrites a stored score unchanged apart from `patch`.
export const dtoToSavePayload = (dto, patch = {}) => ({
  ...buildSavePayload(fromDtoToForm(dto), dto),
  ...patch,
});

// Short label for a score, e.g. "13-14 F 3m 6 dives Div II (2026-08-01 → 2027-07-31)".
export const describeQualifyingScore = (dto) => {
  if (!dto) return "";
  const parts = [
    dto.ageGroup,
    dto.gender,
    heightLabel(dto.eventHeight),
    dto.diveCount != null ? `${dto.diveCount} dives` : null,
    dto.divisionId ? `Div ${dto.divisionId}` : null,
  ].filter(Boolean);
  const dates = dto.fromDate ? ` (${dto.fromDate} → ${dto.toDate || "…"})` : "";
  return `${parts.join(" ")}${dates}`;
};

export const todayIso = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Default diving season: Aug 1 of current year (or previous if today is < Aug 1) → Jul 31 of next.
export const currentSeasonRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const startThisYear = new Date(year, 7, 1); // Aug 1
  const seasonStartYear = today >= startThisYear ? year : year - 1;
  const from = `${seasonStartYear}-08-01`;
  const to = `${seasonStartYear + 1}-07-31`;
  return { from, to };
};

export const addOneYearIso = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return "";
  const yyyy = String(y + 1).padStart(4, "0");
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const formatPoints = (value) => {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
};

// ---- compact list formatting -------------------------------------------------------------------
// Dates arrive as ISO strings without an offset (LocalDate / LocalDateTime). They are formatted
// as written, never through `new Date(...)`, which would shift a date-only value by the browser's
// timezone and turn 2026-09-01 into 8/31 west of UTC.

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const ISO_TIME = /T(\d{2}):(\d{2})/;

/** `2026-09-01` → `9/1/26`; anything unparseable is returned unchanged. */
export const formatShortDate = (value) => {
  if (!value) return null;
  const m = ISO_DATE.exec(String(value));
  if (!m) return String(value);
  return `${Number(m[2])}/${Number(m[3])}/${m[1].slice(2)}`;
};

/** `2026-09-01`, `2027-08-31` → `9/1/26 – 8/31/27`; a missing end reads "onward". */
export const formatDateRange = (from, to) => {
  const start = formatShortDate(from);
  const end = formatShortDate(to);
  if (!start && !end) return null;
  if (!end) return `${start} onward`;
  if (!start) return `until ${end}`;
  return `${start} – ${end}`;
};

/** `2026-09-26T14:05:33.123456` → `9/26/26 2:05 PM` (minutes only). */
export const formatShortDateTime = (value) => {
  if (!value) return null;
  const text = String(value);
  const date = formatShortDate(text);
  const t = ISO_TIME.exec(text);
  if (!t) return date;
  const hours = Number(t[1]);
  const suffix = hours < 12 ? "AM" : "PM";
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${date} ${h12}:${t[2]} ${suffix}`;
};

/** Full timestamp for a tooltip: `2026-09-26 14:05:33`, fractions of a second dropped. */
export const formatFullDateTime = (value) => {
  if (!value) return null;
  return String(value).replace("T", " ").replace(/\.\d+$/, "");
};

/**
 * Create-form values copied from an existing score. The id is not copied (it becomes a new score)
 * and neither is an inactive template, which a new score may not use.
 */
export const copyToForm = (dto, governingBodyId) => ({
  ...fromDtoToForm(dto),
  governingBodyId: governingBodyId ?? dto?.governingBodyId ?? "",
  messageTemplateId:
    dto?.messageTemplateActive === false ? "" : dto?.messageTemplateId ?? "",
});
